import React, { useState, useEffect } from 'react';
import { Send, MapPin, Scroll, Feather } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Guestbook = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    message: '',
    location: '',
    honeypot: '' // Spam protection - hidden field
  });

  const maxMessageLength = 600;

  // Fetch approved entries
  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/guestbook`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
      }
    } catch (err) {
      console.error('Error fetching guestbook entries:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > maxMessageLength) return;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/guestbook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', message: '', location: '', honeypot: '' });
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to submit message');
      }
    } catch (err) {
      setError('Failed to submit message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <section className="guestbook-section" data-testid="guestbook-section">
      <div className="container">
        <div className="section-header">
          <Scroll className="section-icon" size={32} />
          <h2 className="section-title">Write a Love Letter Back</h2>
          <p className="section-subtitle">
            King Spiros wrote A Love Letter To Humanity — now you can write back
          </p>
        </div>

        {/* Submission Form */}
        <div className="guestbook-form-container">
          <div className="parchment-scroll">
            {submitted ? (
              <div className="submission-success" data-testid="submission-success">
                <Feather size={48} className="success-icon" />
                <h3>Thank You, Noble Visitor!</h3>
                <p>Your letter has been received and will appear after approval by the King. 👑</p>
                <button 
                  className="write-another-btn"
                  onClick={() => setSubmitted(false)}
                  data-testid="write-another-btn"
                >
                  Write Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="guestbook-form" data-testid="guestbook-form">
                <div className="form-header">
                  <Feather size={24} />
                  <span>Sign the Royal Scroll</span>
                </div>

                {/* Honeypot field - hidden from humans */}
                <input
                  type="text"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={handleChange}
                  className="honeypot-field"
                  tabIndex="-1"
                  autoComplete="off"
                />

                <div className="form-group">
                  <label htmlFor="name">Your Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    maxLength={100}
                    placeholder="Enter your name"
                    data-testid="guestbook-name-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="location">
                    <MapPin size={14} /> Location (Optional)
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    maxLength={100}
                    placeholder="City, Country"
                    data-testid="guestbook-location-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Your Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={maxMessageLength}
                    rows={4}
                    placeholder="Share your thoughts with the Kingdom..."
                    data-testid="guestbook-message-input"
                  />
                  <div className="character-count">
                    {formData.message.length}/{maxMessageLength}
                  </div>
                </div>

                {error && <div className="form-error" data-testid="form-error">{error}</div>}

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={submitting}
                  data-testid="guestbook-submit-btn"
                >
                  {submitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send size={18} />
                      Send to the Kingdom
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Display Entries */}
        <div className="guestbook-entries" data-testid="guestbook-entries">
          {loading ? (
            <div className="loading-entries">Loading messages from the Kingdom...</div>
          ) : entries.length === 0 ? (
            <div className="no-entries">
              <p>Be the first to leave your mark in the Royal Guestbook!</p>
            </div>
          ) : (
            <div className="entries-grid">
              {entries.map((entry) => (
                <div key={entry.id} className="guestbook-entry" data-testid="guestbook-entry">
                  <div className="entry-scroll">
                    <div className="entry-content">
                      <p className="entry-message">"{entry.message}"</p>
                      <div className="entry-footer">
                        <span className="entry-name">— {entry.name}</span>
                        {entry.location && (
                          <span className="entry-location">
                            <MapPin size={12} /> {entry.location}
                          </span>
                        )}
                      </div>
                      <div className="entry-date">{formatDate(entry.created_at)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Guestbook;
